import { useTranslation } from 'react-i18next';
import { Button, FilterDropdown, InputText, Tag } from '@teo/components';
import { Search } from '@teo/components/icons';
import { useState, useEffect } from 'react';
import { DataTable } from '@/components/DataTable/DataTable';
import { useGet } from '@/query';
import { useParams } from 'react-router-dom';
import { newUid } from '@/util';
import AllCompetenciesModal from '@/pages/Protected/Manage/Competencies/AllCompetenciesModal';
import TextWithTooltip from './TextWithTooltip';
import AddCompetenceModal from '@/components/Competencies/AddCompetenceModal';
import OptionsAction from '@/components/DataTable/OptionsAction';
import { config } from '@/config';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';
import { axios } from '@/axios';
import { canEditCompetence } from '@/components/Competencies/competencePermissions';

const AllCompetencies = ({ categories, competencyType, authUser }: any) => {
  const { t } = useTranslation();
  const { organisationId } = useParams();

  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);
  const [type, setType] = useState<any>();
  const [category, setCategory] = useState<any>();
  const [refetchTable, setRefetchTable] = useState<string>(newUid(20));
  const [isAddModal, setIsAddModal] = useState<any>(false);
  const [isEditModal, setIsEditModal] = useState<any>(false);
  const [activeCompetence, setActiveCompetence] = useState<any>(null);

  const [isOpenDelModal, setIsOpenDelModal] = useState(false);
  const [deleteComId, setDeleteComId] = useState<any>(null);
  const [deleteComPath, setDeleteComPath] = useState<any>(null);

  const handleEdit = (competence: any) => {
    setActiveCompetence(competence);
    document.body.style.overflow = 'hidden';
    setIsEditModal(true);
  };

  return (
    <>
      <div className="py-8 sm:w-[50%]">
        <InputText
          value={filter}
          onChange={(ev) => setFilter(ev.target.value)}
          Icon={Search}
          placeholder={t('pages.manage_competencies.search_placeholder_2')}
        />
      </div>

      <div className="relative z-[1] mb-6 flex flex-col justify-between lg:flex-row lg:items-center">
        <div className="flex flex-row">
          <span className="pr-2">
            <FilterDropdown
              clearButtonLabel="clear"
              onChange={(value: any) => {
                setCategory(value);
              }}
              buttonText={`${t('pages.manage_competencies.all_categories')}`}
              options={categories}
              searchable={true}
              value={category}
            />
          </span>
          {/* <span className="pr-2">
            <FilterDropdown
              clearButtonLabel="clear"
              onChange={(value: any) => {
                setType(value);
              }}
              buttonText={`${t('pages.manage_competencies.all_types')}`}
              options={competencyType}
              searchable={false}
              value={type}
            />
          </span> */}
        </div>
        <div>
          <Button
            onClick={() => {
              document.body.style.overflow = 'hidden';
              setIsAddModal(true);
            }}
            size="sm"
            variant="outline"
            className="mt-6 lg:mt-0"
          >
            {t('pages.manage_competencies.add_competence')}
          </Button>
        </div>
      </div>

      <DataTable
        // pageSize={5}
        filter={debouncedFilter}
        key={refetchTable}
        fetch={useGet.bind(null, '/organisationcompetencedetails', {
          organisationId: `${organisationId},OR,organisationId=null`,
          lang: authUser?.lang,
          disabled: false,
          competenceCategoryId: category?.id,
          // code: 'TES-001',
        })}
        empty={{
          template: 1,
          text: t('empty_tenplate.competencies_list'),
        }}
        columns={[
          {
            addTitleOnMobile: true,
            title: t('columns.code'),
            id: 'code',
            className: 'col-start-1 col-end-5 break-all row-start-2',
            sortable: false,
            classNameDesktop: 'w-[100px]',
            render: (item: any) => {
              return <p className="text-xs text-grey-06">{item?.code}</p>;
            },
          },
          {
            addTitleOnMobile: true,
            title: t('columns.competence'),
            id: 'path',
            className: 'col-start-1 col-end-8 row-start-1',
            classNameDesktop: 'max-w-[450px]',
            render: (item: any) => {
              return (
                <div
                  className={`flex max-w-max items-center rounded-2xl border px-3 py-1 text-black 
                ${
                  item.organisationId === null &&
                  'border-[#FBE49C] bg-[#FFFCEF]'
                }`}
                >
                  {item.organisationId !== null ? (
                    item?.competenceCategoryColorCode && (
                      <span
                        style={{
                          backgroundColor: item?.competenceCategoryColorCode,
                        }}
                        className={`mr-2 h-1.5 w-1.5 min-w-1.5 rounded-full`}
                      ></span>
                    )
                  ) : (
                    <img src="/images/TEO_logo.svg" className="mr-2 h-5 w-5" />
                  )}
                  <p>{item.name}</p>
                  {item?.nrOfLinkedCompetences > 0 && (
                    <div className="ml-2 rounded bg-primary-01 px-1 text-[10px] font-medium text-primary-06">
                      <i className="fas fa-link text-[10px]"></i> TEO
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            addTitleOnMobile: true,
            title: t('columns.category'),
            id: 'competenceCategoryName',
            className:
              'col-start-5 col-end-10 break-all row-start-2 text-right justify-end flex items-center',
            // sortable: false,
            classNameDesktop: 'w-[180px]',
            // align: 'right',
            render: (item: any) => {
              return (
                <>
                  {item?.competenceCategoryName ? (
                    <span className="flex items-center text-grey-08">
                      {item?.competenceCategoryName}
                    </span>
                  ) : (
                    <p className="flex  text-xs text-grey-05">
                      {t('pages.manage_competencies.category_no')}
                    </p>
                  )}
                </>
              );
            },
          },
          /* Hidden -- uncomment to re-enable the courses column. If
             re-enabling, also revert the "profiles" column's className
             below (col-start-5 col-end-10) so it no longer spans the
             full row. */
          // {
          //   addTitleOnMobile: true,
          //   title: t('courseTypes.courses'),
          //   id: 'courses',
          //   className: 'col-start-1 col-end-5 row-start-3',
          //   align: 'right',
          //   sortable: false,
          //   classNameDesktop: 'w-[100px]',
          //   render: (item: any) => {
          //     return <p className="text-xs">{item?.nrOfCourses || 0}</p>;
          //   },
          // },
          {
            addTitleOnMobile: true,
            title: t('pages.manage_competencies.competency_profiles_tab'),
            id: 'profiles',
            align: 'right',
            className:
              'col-start-1 col-end-10 row-start-3 text-right justify-end flex items-center',
            sortable: false,
            classNameDesktop: 'w-[100px]',
            render: (item: any) => {
              return <p className="text-xs">{item?.nrOfProfiles || 0}</p>;
            },
          },
          {
            title: '',
            id: 'edit',
            sortable: false,
            className: 'col-start-9 row-start-1 justify-end flex items-center',
            classNameDesktop: 'w-[100px]',
            render: (item: any) => {
              const canEdit = canEditCompetence(item, authUser?.role);

              return (
                <div className="flex justify-end">
                  {canEdit && (
                    <OptionsAction
                      delete={{
                        title: t('pages.manage_competencies.delete_competence'),
                        onClick: () => {
                          setDeleteComPath(item?.name);
                          setDeleteComId(item.competenceId);
                          setIsOpenDelModal(true);
                        },
                        // request: `${config.backend}/competence/${item.competenceId}`,
                      }}
                      edit={{
                        title: t('pages.manage_competencies.edit_competence'),
                        request: () => handleEdit(item),
                      }}
                      rowInfo={item}
                      screensUpdateTable="mobile"
                      setRefetchTable={setRefetchTable}
                      mobileHeader={{
                        render: (item: any) => {
                          return item.path;
                        },
                      }}
                    />
                  )}
                </div>
              );
            },
          },
        ]}
      />
      {isOpenDelModal && (
        <ComponentDeleteModal
          closeModal={() => setIsOpenDelModal(false)}
          setIsOpenDeleteModal={setIsOpenDelModal}
          title={t('delete_modal.competencies.title')}
          heading={t('delete_modal.competencies.heading')}
          text={
            t('delete_modal.competencies.text_1') +
            `<b>${deleteComPath}</b>` +
            t('delete_modal.competencies.text_2')
          }
          handleDelete={() => {
            axios
              .delete(`${config.backend}/competence/${deleteComId}`)
              .then(() => {
                setDeleteComId(null);
                setDeleteComPath(null);
                setRefetchTable(newUid(20));
                setIsOpenDelModal(false);
              });
          }}
        />
      )}
      {isAddModal && (
        <AddCompetenceModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsAddModal(false);
          }}
          setIsOpenAddModal={setIsAddModal}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          setActiveCompetence={null}
          activeCompetence={null}
        />
      )}
      {isEditModal && (
        <AddCompetenceModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsEditModal(false);
          }}
          setIsOpenAddModal={setIsEditModal}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          edit={true}
          setActiveCompetence={setActiveCompetence}
          activeCompetence={activeCompetence}
        />
      )}
      {/* {isOpenAllCompetenciesModal && (
        <AllCompetenciesModal
          setIsOpenAllCompetenciesModal={setIsOpenAllCompetenciesModal}
          isOpenAllCompetenciesModal={isOpenAllCompetenciesModal}
          closeModal={() => setIsOpenAllCompetenciesModal(false)}
          onChange={() => setRefetchTable(newUid(20))}
          isAddModal={isAddModal}
          isTagIndex={isAllCompetenciesIndex}
        />
      )} */}
    </>
  );
};

export default AllCompetencies;
